import {EditorSelection, type Extension, StateEffect, StateField} from '@codemirror/state'
import {EditorView} from '@codemirror/view'

import {overlapsNode} from '../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {findWordBoundaries} from '../utils'
import {getActiveNodes} from './active-nodes'

/**
 * Extension to fix double-click selection behavior when clicking near links or other elements
 * that expand on activation.
 *
 * The problem: When double-clicking text adjacent to a link, the first click triggers active node
 * updates that cause visual layout shifts (e.g., link delimiters appearing/disappearing via CSS).
 * While the document positions don't change, these visual changes can confuse the browser's native
 * double-click word selection logic, causing incorrect text to be selected.
 *
 * The solution: Track click positions at the mousedown level (before active node updates) and use
 * that position to calculate word boundaries on double-click.
 */

interface ClickState {
  position: number
  timestamp: number
  wasInActiveNode: boolean
  hadMouseMove: boolean
}

// Double-click detection window (ms)
const DOUBLE_CLICK_TIME_WINDOW = 500

/**
 * Effect to update the click state.
 */
const updateClickStateEffect = StateEffect.define<ClickState | null>()

/**
 * StateField that tracks the last click state.
 */
const clickStateField = StateField.define<ClickState | null>({
  create: () => null,
  update: (state, tr) => {
    for (const effect of tr.effects) {
      if (effect.is(updateClickStateEffect)) {
        return effect.value
      }
    }
    return state
  },
})

/**
 * Event handlers for tracking clicks and handling double-clicks.
 */
const doubleClickHandler = EditorView.domEventHandlers({
  mousedown: (event, view) => {
    // Only track left clicks
    if (event.button !== 0) return false

    const now = Date.now()
    const pos = view.posAtCoords({x: event.clientX, y: event.clientY})
    if (pos === null) return false

    const lastClickState = view.state.field(clickStateField)

    // Check if this is the second mousedown in a double-click sequence
    if (lastClickState && now - lastClickState.timestamp < DOUBLE_CLICK_TIME_WINDOW) {
      // This is the second click - don't update click state, keep the first click position
      // Don't prevent default here - allow normal browser behavior for drag-to-select
      return false
    }

    // This is a first click - store this position (captured before any active node updates)
    // Also check if this position is currently in an active node
    const activeNodes = getActiveNodes(view.state)
    const wasInActiveNode = Array.from(activeNodes).some(node => {
      if (!isPositionedNode(node)) return false
      // Skip the root node - it's always active and spans the entire document
      if (node.type === 'root') return false
      return overlapsNode(pos, pos, node.position.start.offset, node.position.end.offset)
    })

    view.dispatch({
      effects: updateClickStateEffect.of({
        position: pos,
        timestamp: now,
        wasInActiveNode,
        hadMouseMove: false,
      }),
    })

    return false
  },

  mousemove: (event, view) => {
    // Track if mouse moved during a click sequence
    const lastClickState = view.state.field(clickStateField)
    if (lastClickState && !lastClickState.hadMouseMove) {
      view.dispatch({
        effects: updateClickStateEffect.of({
          ...lastClickState,
          hadMouseMove: true,
        }),
      })
    }
    return false
  },

  dblclick: (event, view) => {
    // Handle double-click selection using the stored first click position
    const lastClickState = view.state.field(clickStateField)

    if (!lastClickState) {
      // No stored click state from the first mousedown
      return false
    }

    const firstClickPos = lastClickState.position
    const text = view.state.doc.toString()

    // Check if the first click was already in an active node
    // If so, use default browser behavior since the delimiters were already visible
    if (lastClickState.wasInActiveNode) {
      // First click was already in an active node, let browser handle it
      view.dispatch({
        effects: updateClickStateEffect.of(null),
      })
      return false
    }

    // Check if the mouse moved during the click sequence - this indicates a drag selection
    if (lastClickState.hadMouseMove) {
      // User dragged to select, preserve the browser's selection
      view.dispatch({
        effects: updateClickStateEffect.of(null),
      })
      return false
    }

    // Prevent the default double-click behavior
    event.preventDefault()
    event.stopPropagation()

    // Calculate word boundaries at the first click position
    const {start: wordStart, end: wordEnd} = findWordBoundaries(text, firstClickPos)

    if (wordStart !== wordEnd) {
      // Set the selection
      view.dispatch({
        selection: EditorSelection.range(wordStart, wordEnd),
        effects: [updateClickStateEffect.of(null)],
        userEvent: 'select.pointer',
      })

      return true
    }

    // Reset click state
    view.dispatch({
      effects: updateClickStateEffect.of(null),
    })
    return false
  },
})

/**
 * Extension that improves double-click selection behavior by tracking click positions
 * and fixing selection after layout shifts caused by active node changes.
 */
export const doubleClickSelectionExtension: Extension = [clickStateField, doubleClickHandler]
